import { isMarkdownDocumentPath } from '@src/lib/markdown-path';
import { AssetDocPointer } from './AssetDocPointer';
import { AssetEditor, editorForPath, LOCAL_COMPUTE_NODE } from './asset-doc-types';
import { DockPointer } from './DockPointer';
import { isAbsoluteMachinePath } from '@sdk';
import { normalizeAssetVfsPath } from './asset-doc-pointer-grammar';

/**
 * THE pointer-level chokepoint for every generic "open this file" surface —
 * file browsers, chat attachments, task artifacts, `navigation.openFile`.
 * Routing is the shared `editorForPath` extension rule: markdown → markdown
 * editor, html → sandboxed preview, images/video/audio → media viewer,
 * everything else → the code editor. Callers that explicitly want the code
 * editor (an "Open in Editor" affordance, code hook sources) use
 * `DockPointer.forFile` directly.
 *
 * NOTE: markdown keeps the wider `isMarkdownDocumentPath` predicate — the
 * registry's `markdown` shape (`shape.ext` + `also`) plus the display-only
 * spellings (mdx, md.out, …); `editorForPath` reads the same registry set
 * without the extras. Built via `AssetDocPointer.forVfs`, NOT
 * `DockPointer.forAssetEditor` (that takes a record type).
 */
export function dockPointerForFile(
  path: string,
  options?: { line?: number; column?: number },
): DockPointer {
  const editor = isMarkdownDocumentPath(path) ? AssetEditor.MARKDOWN : editorForPath(path);
  if (editor !== AssetEditor.CODE) {
    // Carry the line across too. The asset editors name it `initialLine`
    // (`MilkdownEditor`'s "place the caret here on mount" prop) rather than the
    // code editor's `line`, so translate instead of dropping it — without this
    // an "open at line" into a .md target silently loses the line.
    const assetOptions = options?.line ? { initialLine: String(options.line) } : undefined;
    return AssetDocPointer.forVfs(editor, path, undefined, assetOptions).toDockPointer();
  }
  // CodeEditor consumes a VFS address. A raw absolute path would otherwise be
  // treated as project-relative, duplicating the project root during download.
  const address = isAbsoluteMachinePath(path) ? normalizeAssetVfsPath(path, LOCAL_COMPUTE_NODE).absVfsPath : path;
  return DockPointer.forFile(address, options);
}
